import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { markAllNotificationsRead, markNotificationRead, NOTIFICATION_KEYS, type AppNotification } from '../../api/notifications'
import { useToastStore } from '../../store/toastStore'

/**
 * Otevření notifikace (označit přečtenou + přejít na odkaz) a „Označit vše jako přečtené".
 * Sdílené panelem u zvonečku i stránkou /notifications.
 */
export function useNotificationActions(onNavigated?: () => void) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const { addToast } = useToastStore()

  const refresh = () => queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all })

  const markOne = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: (res) => {
      queryClient.setQueryData(NOTIFICATION_KEYS.unread, res.unread_count)
      refresh()
    },
  })

  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.setQueryData(NOTIFICATION_KEYS.unread, 0)
      refresh()
    },
    onError: () => addToast('error', 'Notifikace se nepodařilo označit jako přečtené'),
  })

  const openNotification = (n: AppNotification) => {
    if (n.read_at === null) markOne.mutate(n.id)
    onNavigated?.()
    if (!n.link) return
    // Detail úkolu je modal nad aktuální stránkou (zavřením se uživatel vrátí, kde byl).
    if (n.link.startsWith('/tasks/detail/')) {
      navigate(n.link, { state: { backgroundLocation: location } })
    } else {
      navigate(n.link)
    }
  }

  return { openNotification, markAll: () => markAllMutation.mutate(), markingAll: markAllMutation.isPending }
}
